import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Wrench,
  Users,
  Trees,
  ShieldCheck,
  Hammer,
  Zap,
  Award,
  Handshake,
  Leaf,
  Package,
  Building2,
  Factory,
  Cross,
  Hotel,
  Landmark,
  Store,
  Warehouse,
  Home as HomeIcon,
  Building,
  UserCheck,
  BadgeCheck,
  GraduationCap,
  Clock,
  CheckCircle2,
  TrendingUp,
} from "lucide-react";
import { SERVICES, VALEURS, METHODOLOGIE, SECTEURS, ENGAGEMENTS } from "@/lib/content";

const ICONS = {
  Sparkles, Wrench, Users, Trees, ShieldCheck, Hammer, Zap, Award, Handshake, Leaf, Package,
  Building, Building2, Factory, Cross, Hotel, Home: HomeIcon, Landmark, Store, Warehouse,
  UserCheck, BadgeCheck, GraduationCap, Clock, CheckCircle2, TrendingUp,
};

const Icon = ({ name, ...p }) => {
  const C = ICONS[name] || CheckCircle2;
  return <C {...p} />;
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ============ HERO — matches Image 1 exactly ============ */}
      <section
        className="relative overflow-hidden text-white"
        style={{ paddingTop: "180px", paddingBottom: "120px", background: "#0A2540" }}
        data-testid="hero"
      >
        {/* Skyscraper background image with blue filter */}
        <div className="absolute inset-0">
          <img
            src="/photos/cover-skyscraper.jpg"
            alt="Immeuble tertiaire premium — NOVANEXT Facility Management"
            className="w-full h-full object-cover nx-kenburns"
            style={{ objectPosition: "center right" }}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          {/* Blue tint overlay (matches Image 1 look) */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(10,37,64,0.95) 0%, rgba(10,37,64,0.85) 45%, rgba(10,37,64,0.55) 100%)",
            }}
          ></div>
          <div
            className="absolute inset-0"
            style={{ background: "rgba(10, 37, 64, 0.35)" }}
          ></div>
        </div>

        <div className="nx-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* LEFT — Text block */}
            <div className="lg:col-span-7 nx-fade-up nx-hero-left">
              <div
                style={{
                  fontFamily: "Exo 2, sans-serif",
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "#00C8FF",
                }}
              >
                Facility Management · Multi-expertises
              </div>

              <h1
                className="text-white nx-hero-h1"
                style={{
                  fontFamily: "Exo 2, sans-serif",
                  fontWeight: 700,
                  lineHeight: 1.08,
                  letterSpacing: "-0.005em",
                  textTransform: "uppercase",
                  marginTop: 28,
                  marginBottom: 0,
                }}
              >
                Un partenaire unique
                <br />
                <span style={{ color: "#00C8FF" }}>pour tous vos services</span>
              </h1>

              {/* Cyan divider */}
              <div
                style={{
                  width: 120,
                  height: 3,
                  background: "#00C8FF",
                  marginTop: 40,
                  marginBottom: 40,
                }}
              ></div>

              <p
                style={{
                  fontFamily: "Exo 2, sans-serif",
                  fontWeight: 400,
                  fontSize: 18,
                  lineHeight: 1.75,
                  color: "#FFFFFF",
                  maxWidth: 620,
                  marginBottom: 40,
                }}
              >
                Un interlocuteur unique pour l'ensemble de vos services généraux&nbsp;: propreté, maintenance, travaux, espaces verts et multiservices. Vous récupérez du temps pour votre cœur de métier.
              </p>

              <div className="flex flex-wrap" style={{ gap: 16 }}>
                <Link
                  to="/contact"
                  className="inline-flex items-center transition-colors duration-200"
                  style={{
                    gap: 12,
                    height: 54,
                    padding: "0 32px",
                    borderRadius: 6,
                    background: "#00C8FF",
                    color: "#FFFFFF",
                    fontFamily: "Exo 2, sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#0099CC"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#00C8FF"; }}
                  data-testid="hero-cta-devis"
                >
                  Demander un devis
                  <ArrowRight size={16} strokeWidth={2.25} />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center transition-all duration-200"
                  style={{
                    gap: 12,
                    height: 54,
                    padding: "0 32px",
                    borderRadius: 6,
                    background: "transparent",
                    color: "#FFFFFF",
                    border: "1.5px solid rgba(255,255,255,0.5)",
                    fontFamily: "Exo 2, sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#00C8FF";
                    e.currentTarget.style.color = "#00C8FF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)";
                    e.currentTarget.style.color = "#FFFFFF";
                  }}
                  data-testid="hero-cta-services"
                >
                  Découvrir nos pôles
                </Link>
              </div>
            </div>

            {/* RIGHT — 2x2 translucent cards */}
            <div className="lg:col-span-5 nx-fade-up nx-delay-2 nx-hero-right">
              <div className="grid grid-cols-2" style={{ gap: 20 }}>
                {[
                  { icon: Award, title: "EXPERTISE", sub: "Un savoir-faire reconnu" },
                  { icon: ShieldCheck, title: "FIABILITÉ", sub: "Des engagements tenus" },
                  { icon: Users, title: "PROXIMITÉ", sub: "Une présence terrain" },
                  { icon: Leaf, title: "RESPONSABILITÉ", sub: "Un impact durable" },
                ].map((c, i) => {
                  const IconC = c.icon;
                  return (
                    <div
                      key={c.title}
                      className={`nx-fade-up nx-delay-${i + 1} transition-all duration-300`}
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        padding: 28,
                        minHeight: 200,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        backdropFilter: "blur(6px)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(0,200,255,0.1)";
                        e.currentTarget.style.borderColor = "rgba(0,200,255,0.4)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                        e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <IconC size={30} strokeWidth={1.5} style={{ color: "#00C8FF" }} />
                      <div style={{ marginTop: 20 }}>
                        <div
                          style={{
                            fontFamily: "Exo 2, sans-serif",
                            fontWeight: 700,
                            fontSize: 16,
                            letterSpacing: "0.03em",
                            color: "#FFFFFF",
                            marginBottom: 8,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {c.title}
                        </div>
                        <div
                          style={{
                            fontFamily: "Exo 2, sans-serif",
                            fontWeight: 400,
                            fontSize: 14,
                            lineHeight: 1.5,
                            color: "rgba(255,255,255,0.8)",
                          }}
                        >
                          {c.sub}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ OFFRE GLOBALE (Facility Management) ============ */}
      <section className="bg-white nx-arch-grid-light" style={{ paddingTop: 120, paddingBottom: 120 }} data-testid="offre-globale">
        <div className="nx-container">
          <div className="max-w-3xl">
            <span className="nx-overline">Une offre intégrée</span>
            <h2 style={{ marginTop: 24, marginBottom: 32 }}>
              Vous confiez tout à un seul partenaire.
            </h2>
            <p style={{ marginBottom: 0 }}>
              Propreté, maintenance, rénovation, espaces verts, multiservices — et, en complément, la fourniture de vos consommables et la gestion de vos déchets. Vous centralisez la gestion opérationnelle de vos sites au sein d'un contrat unique, piloté de bout en bout par nos équipes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: 30, marginTop: 80 }}>
            {VALEURS.map((v, i) => (
              <div key={v.title} className={`nx-card nx-fade-up nx-delay-${i + 1}`} data-testid={`valeur-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="nx-icon-wrap"><Icon name={v.icon} size={26} strokeWidth={1.75} /></div>
                <h4 style={{ marginTop: 24, marginBottom: 16 }}>{v.title}</h4>
                <p style={{ fontSize: 16, marginBottom: 0 }}>{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICES / EXPERTISES ============ */}
      <section className="bg-white nx-arch-skyline-light" style={{ paddingTop: 0, paddingBottom: 120 }} data-testid="services-preview">
        <div className="nx-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" style={{ marginBottom: 24 }}>
            <div className="max-w-2xl">
              <span className="nx-overline">Nos expertises</span>
              <h2 style={{ marginTop: 24, marginBottom: 0 }}>
                Cinq pôles, une seule signature de service.
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-cyan hover:text-navy transition-colors" style={{ fontFamily: "Exo 2", fontWeight: 600, fontSize: 15, letterSpacing: "0.02em" }} data-testid="services-see-all">
              Découvrir nos prestations <ArrowRight size={16} strokeWidth={2} />
            </Link>
          </div>
          <p style={{ marginBottom: 64, maxWidth: 800, fontSize: 17 }}>
            Toutes vos prestations sont coordonnées de bout en bout pour garantir une qualité constante et un suivi simplifié — moins de coordination pour vous, plus de résultats sur vos sites.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: 30 }}>
            {SERVICES.map((s, i) => (
              <Link
                to={`/services#${s.id}`}
                key={s.id}
                className={`nx-card group nx-fade-up nx-delay-${(i % 4) + 1}`}
                data-testid={`service-card-${s.id}`}
                style={{ display: "block" }}
              >
                <div className="nx-icon-wrap"><Icon name={s.icon} size={26} strokeWidth={1.75} /></div>
                <h4 style={{ marginTop: 24, marginBottom: 12 }}>{s.title}</h4>
                <p style={{ fontSize: 16, marginBottom: 24 }} className="nx-line-clamp-2">{s.tagline}</p>
                <div className="inline-flex items-center gap-2 text-cyan" style={{ fontSize: 15, fontWeight: 600 }}>
                  En savoir plus <ArrowRight size={16} strokeWidth={2} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ METHODOLOGIE — Notre engagement qualité 6 étapes ============ */}
      <section className="bg-navy text-white relative overflow-hidden nx-arch-skyline-dark" style={{ paddingTop: 40, paddingBottom: 100 }} data-testid="methodologie">
        <div
          className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full opacity-15"
          style={{ background: "radial-gradient(circle, #00C8FF 0%, transparent 70%)" }}
        ></div>
        <div className="nx-container relative">
          <div className="max-w-2xl">
            <span className="nx-overline">Notre engagement qualité</span>
            <h2 className="text-white" style={{ marginTop: 24, marginBottom: 32 }}>
              Chaque intervention est contrôlée.
            </h2>
            <p className="text-mist" style={{ marginBottom: 0 }}>
              Un processus rigoureux, appliqué de la première visite à l'amélioration continue — pour un niveau de service constant sur la durée.
            </p>
          </div>

          <div style={{ maxWidth: 900, marginTop: 40, marginLeft: "auto", marginRight: "auto" }}>
            {METHODOLOGIE.map((m, i) => (
              <div
                key={m.step}
                className={`nx-fade-up nx-delay-${(i % 4) + 1}`}
                style={{
                  display: "flex",
                  gap: 32,
                  paddingBottom: 40,
                  position: "relative",
                }}
              >
                {/* Left col — step number + line */}
                <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 10,
                      background: "#00C8FF",
                      color: "#0D2B45",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "Exo 2",
                      fontWeight: 700,
                      fontSize: 20,
                      flexShrink: 0,
                      zIndex: 2,
                    }}
                  >
                    {m.step}
                  </div>
                  {i < METHODOLOGIE.length - 1 && (
                    <div
                      style={{
                        position: "absolute",
                        top: 56,
                        bottom: -16,
                        width: 2,
                        background: "linear-gradient(to bottom, rgba(0,200,255,0.4), rgba(0,200,255,0.1))",
                      }}
                    />
                  )}
                </div>
                {/* Right col — content */}
                <div style={{ flex: 1, paddingTop: 8 }}>
                  <h4 className="text-white" style={{ marginBottom: 12, fontSize: 22 }}>{m.title}</h4>
                  <p className="text-mist" style={{ fontSize: 16, marginBottom: 0, maxWidth: 620 }}>{m.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTEURS ============ */}
      <section className="bg-white" style={{ paddingTop: 120, paddingBottom: 120 }} data-testid="secteurs">
        <div className="nx-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4">
              <span className="nx-overline">Secteurs d'intervention</span>
              <h2 style={{ marginTop: 24, marginBottom: 32 }}>
                Vos contraintes métier, comprises.
              </h2>
              <p style={{ marginBottom: 40 }}>
                Que vous gériez des bureaux, un site industriel, une collectivité, un établissement de santé ou un hôtel&nbsp;: nos équipes s'adaptent aux impératifs spécifiques de votre secteur d'activité.
              </p>
              <Link to="/faq" className="inline-flex items-center gap-2 text-cyan hover:text-navy transition-colors" style={{ fontFamily: "Exo 2", fontWeight: 600, fontSize: 15, letterSpacing: "0.02em" }} data-testid="secteurs-cta">
                Consulter les questions fréquentes <ArrowRight size={16} strokeWidth={2} />
              </Link>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: 16 }}>
                {SECTEURS.map((s, i) => (
                  <div
                    key={s.title}
                    className={`nx-card nx-fade-up nx-delay-${(i % 4) + 1}`}
                    style={{ padding: 24, display: "flex", alignItems: "center", gap: 16 }}
                  >
                    <Icon name={s.icon} size={22} strokeWidth={1.75} className="text-cyan shrink-0" />
                    <span style={{ fontSize: 15, fontWeight: 500, color: "#0D2B45" }}>{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ENGAGEMENTS — Extrait 3 items + lien vers /pourquoi-nous (liste canonique) ============ */}
      <section className="bg-white nx-arch-grid-light" style={{ paddingTop: 0, paddingBottom: 120 }} data-testid="engagements">
        <div className="nx-container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="nx-overline">Nos engagements</span>
            <h2 style={{ marginTop: 24, marginBottom: 32 }}>
              Ce que vous obtenez, à chaque intervention.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 30, marginTop: 64 }}>
            {ENGAGEMENTS.slice(0, 3).map((e, i) => (
              <div key={e.title} className={`nx-card nx-fade-up nx-delay-${(i % 4) + 1}`} data-testid={`home-engagement-${i}`}>
                <div className="nx-icon-wrap"><Icon name={e.icon} size={24} strokeWidth={1.75} /></div>
                <h4 style={{ marginTop: 24, marginBottom: 12 }}>{e.title}</h4>
                <p style={{ fontSize: 16, marginBottom: 0 }}>{e.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center" style={{ marginTop: 48 }}>
            <Link
              to="/pourquoi-nous"
              className="inline-flex items-center gap-2 text-cyan hover:text-navy transition-colors"
              style={{ fontFamily: "Exo 2", fontWeight: 600, fontSize: 16, letterSpacing: "0.02em" }}
              data-testid="engagements-see-all"
            >
              Découvrir tous nos engagements <ArrowRight size={16} strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>


      {/* ============ CTA FINAL ============ */}
      <section className="bg-ink text-white relative overflow-hidden nx-arch-skyline-dark" style={{ paddingTop: 40, paddingBottom: 100 }} data-testid="cta-final">
        <div className="absolute inset-0 nx-hero-grid opacity-40"></div>
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #00C8FF 0%, transparent 70%)" }}
        ></div>
        <div className="nx-container relative text-center">
          <span className="nx-overline">Parlons de vos sites</span>
          <h2 className="text-white" style={{ marginTop: 24, marginBottom: 32, maxWidth: 900, marginLeft: "auto", marginRight: "auto" }}>
            Recevez une proposition claire, chiffrée et adaptée à vos sites — sous 24h ouvrées.
          </h2>
          <p className="text-mist" style={{ maxWidth: 620, marginLeft: "auto", marginRight: "auto", marginBottom: 40 }}>
            Un devis clair, une équipe dédiée, une transparence totale. Vous gagnez du temps dès le premier échange.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="nx-btn nx-btn-primary" data-testid="cta-final-devis">
              Demander un devis <ArrowRight size={18} strokeWidth={2} />
            </Link>
            <a href="tel:+33189628787" className="nx-btn nx-btn-outline" data-testid="cta-final-phone">
              01 89 62 87 87
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

